import axiosInstance from '../../core/axiosConfig.ts';

/**
 * Attractions curation, the admin's side (ADMIN role only, enforced by the
 * backend). The contract is attractions-api-contract.md, section 4 "Admin":
 * Viator's products are cached by the backend; an admin switches individual
 * products In app, tags them with Vello's collections and sets the automatic
 * rules that hide the rest.
 */

export type DurationMinutes = { min: number; max: number };

export type RuleReason = 'REVIEWS' | 'RATING' | 'NIGHTLIFE' | 'SATURDAY_ONLY' | 'MULTI_DAY' | 'TRANSFER' | 'GONE';
export type HiddenReason = 'DUPLICATE' | 'ADMIN';
export type CatalogueFilter = 'all' | 'in_app' | 'hidden' | 'queue' | 'overridden';

export type AdminProduct = {
    code: string;
    title: string;
    image: { url: string; width: number; height: number } | null;
    supplierName: string | null;
    rating: number | null;
    reviewCount: number | null;
    fromPrice: number | null;
    currency: string | null;
    durationMinutes: DurationMinutes | null;
    durationText: string | null;
    category: string | null;
    inApp: boolean;
    /** Set by an automatic rule (the first one failed): without an override the product cannot be switched on. */
    ruleReason: RuleReason | null;
    /** Admin override of the rules: switchable In app although ruleReason stays set (never for GONE). */
    ruleOverride?: boolean | null;
    ruleOverrideAt?: string | null;
    /** Email of the admin who overrode. */
    ruleOverrideBy?: string | null;
    /** Every rule failed right now, e.g. "Min reviews (32 < 50)"; empty when ruleReason is null. */
    ruleFailures?: string[] | null;
    /** Set by an admin ("Keep best-rated" or a manual hide). */
    hiddenReason: HiddenReason | null;
    warnings: string[] | null;
    /** Slugs of the collections the product belongs to. */
    collections: string[] | null;
    bookingUrl: string | null;
    lastSyncedAt: string | null;
    area: string | null;
    freeCancellation: boolean | null;
};

export type CatalogueGroup = {
    /** null for the "Other products" bucket. */
    groupId: string | null;
    label: string;
    duplicate: boolean;
    productCount: number;
    inAppCount: number;
    products: AdminProduct[];
};

export type CatalogueTiles = {
    fromViator: number;
    removedByRules: number;
    inReviewQueue: number;
    liveInApp: number;
    queueGroups: number;
};

export type CatalogueCity = {
    cityId: number;
    name: string;
    liveInApp: number;
    total: number;
    viatorDestinationId: string | null;
    lastSyncedAt: string | null;
};

export type SyncCity = { cityId: number; fetched: number; detailed: number; error: string | null };

export type SyncStatus = {
    running: boolean;
    startedAt: string | null;
    finishedAt: string | null;
    message: string | null;
    cities?: SyncCity[];
};

export type Catalogue = {
    cityId: number | null;
    tiles: CatalogueTiles;
    cities: CatalogueCity[];
    lastSync: SyncStatus | null;
    groups: CatalogueGroup[];
};

export type AttractionCollection = {
    id: number;
    slug: string;
    title: string;
    titleHe: string | null;
    subtitle: string | null;
    subtitleHe: string | null;
    sortOrder: number;
    active: boolean;
    productCount: number;
    productCodes: string[];
};

export type CollectionBody = {
    slug: string;
    title: string;
    titleHe?: string | null;
    subtitle?: string | null;
    subtitleHe?: string | null;
    sortOrder?: number;
    active?: boolean;
};

export type AttractionRules = {
    minReviews: number;
    minRating: number;
    hideNightlife: boolean;
    hideSaturdayOnly: boolean;
    hideMultiDay: boolean;
    multiDayHours: number;
    groupDuplicates: boolean;
    hideTransfers: boolean;
    updatedAt?: string | null;
};

/** Which plan dates: from today on (Cyprus), before today, or every one. */
export type BookingsWhen = 'upcoming' | 'past' | 'all';

/**
 * An attraction a customer marked as booked in the app ("Did you book it?" → Yes)
 * after booking it on Viator. Their own word: confirmed bookings and commission
 * are in Viator's partner dashboard.
 */
export type AttractionBooking = {
    id: number;
    /** YYYY-MM-DD, the day the customer plans to go. */
    planDate: string;
    /** HH:mm, null when the customer gave no time. */
    startTime: string | null;
    productCode: string;
    title: string;
    cityId: number | null;
    cityName: string | null;
    thumbnailUrl: string | null;
    /** The product's page on Viator, null when not stored. */
    viatorUrl: string | null;
    customerId: number;
    customerName: string | null;
    customerEmail: string | null;
    /** When the customer said they booked it. */
    markedAt: string;
};

export type BookingsQuery = {
    when?: BookingsWhen;
    from?: string | null;
    to?: string | null;
    cityId?: number | null;
    customerId?: number | null;
    q?: string;
};

export type Paged<T> = { rows: T[]; total: number };

const enc = encodeURIComponent;

export default class AttractionsService {
    async catalogue(params: { cityId?: number | null; filter?: CatalogueFilter; q?: string }): Promise<Catalogue> {
        const p: Record<string, string | number> = {};
        if (params.cityId) p.cityId = params.cityId;
        if (params.filter) p.filter = params.filter;
        if (params.q && params.q.trim()) p.q = params.q.trim();
        const response = await axiosInstance.get('/admin/attractions/catalogue', { params: p });
        const data = response.data ?? {};
        return {
            cityId: data.cityId ?? null,
            tiles: data.tiles ?? { fromViator: 0, removedByRules: 0, inReviewQueue: 0, liveInApp: 0, queueGroups: 0 },
            cities: Array.isArray(data.cities) ? data.cities : [],
            lastSync: data.lastSync ?? null,
            groups: Array.isArray(data.groups) ? data.groups : [],
        };
    }

    /** Body `{ inApp }` and/or `{ hidden }`. 409 error.attraction_hidden_by_rule when a rule removed it. */
    async updateProduct(code: string, body: { inApp?: boolean; hidden?: boolean; ruleOverride?: boolean }): Promise<AdminProduct> {
        const response = await axiosInstance.patch(`/admin/attractions/products/${enc(code)}`, body);
        return response.data;
    }

    async keepBest(groupId: string): Promise<{ kept: string; hidden: string[] }> {
        const response = await axiosInstance.post(`/admin/attractions/groups/${enc(groupId)}/keep-best`);
        return response.data ?? { kept: '', hidden: [] };
    }

    async collections(): Promise<AttractionCollection[]> {
        const response = await axiosInstance.get('/admin/attractions/collections');
        return Array.isArray(response.data) ? response.data : [];
    }

    async createCollection(body: CollectionBody): Promise<AttractionCollection> {
        const response = await axiosInstance.post('/admin/attractions/collections', body);
        return response.data;
    }

    async updateCollection(id: number, body: CollectionBody): Promise<AttractionCollection> {
        const response = await axiosInstance.put(`/admin/attractions/collections/${id}`, body);
        return response.data;
    }

    async deleteCollection(id: number): Promise<{ id: number; deleted: boolean }> {
        const response = await axiosInstance.delete(`/admin/attractions/collections/${id}`);
        return response.data;
    }

    async addToCollection(id: number, code: string): Promise<AttractionCollection> {
        const response = await axiosInstance.put(`/admin/attractions/collections/${id}/products/${enc(code)}`);
        return response.data;
    }

    async removeFromCollection(id: number, code: string): Promise<AttractionCollection> {
        const response = await axiosInstance.delete(`/admin/attractions/collections/${id}/products/${enc(code)}`);
        return response.data;
    }

    async rules(): Promise<AttractionRules> {
        const response = await axiosInstance.get('/admin/attractions/rules');
        return response.data;
    }

    /** Re-applies the rules to the stored products at once (no Viator call). */
    async saveRules(rules: AttractionRules): Promise<AttractionRules> {
        const { updatedAt, ...body } = rules;
        const response = await axiosInstance.put('/admin/attractions/rules', body);
        return response.data;
    }

    /** Paged like the panel's other lists: _start/_end, the total in X-Total-Count. */
    async bookings(query: BookingsQuery, start: number, end: number): Promise<Paged<AttractionBooking>> {
        const p: Record<string, string | number> = { _start: start, _end: end, when: query.when ?? 'upcoming' };
        if (query.from) p.from = query.from;
        if (query.to) p.to = query.to;
        if (query.cityId) p.cityId = query.cityId;
        if (query.customerId) p.customerId = query.customerId;
        if (query.q && query.q.trim()) p.q = query.q.trim();
        const response = await axiosInstance.get('/admin/attractions/bookings', { params: p });
        const rows: AttractionBooking[] = Array.isArray(response.data) ? response.data : [];
        const total = Number(response.headers?.['x-total-count'] ?? response.headers?.['X-Total-Count']);
        return { rows, total: Number.isFinite(total) ? total : rows.length };
    }

    async startSync(cityId?: number | null): Promise<SyncStatus> {
        const response = await axiosInstance.post('/admin/attractions/sync', undefined, cityId ? { params: { cityId } } : undefined);
        return response.data;
    }

    async syncStatus(): Promise<SyncStatus> {
        const response = await axiosInstance.get('/admin/attractions/sync');
        return response.data;
    }
}

export const attractionsService = new AttractionsService();
